"use client";
import { EditorContent } from "@tiptap/react";

import * as React from "react";

// --- UI Primitives ---
import { Button } from "../../tiptap-ui-primitive/button";
import { Spacer } from "../../tiptap-ui-primitive/spacer";
import {
  Toolbar as TiptapToolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "../../tiptap-ui-primitive/toolbar";

// --- Tiptap Node ---
import "../../tiptap-node/blockquote-node/blockquote-node.scss";
import "../../tiptap-node/code-block-node/code-block-node.scss";
import "../../tiptap-node/heading-node/heading-node.scss";
import "../../tiptap-node/horizontal-rule-node/horizontal-rule-node.scss";
import "../../tiptap-node/image-node/image-node.scss";
import "../../tiptap-node/list-node/list-node.scss";
import "../../tiptap-node/paragraph-node/paragraph-node.scss";

// --- Tiptap UI ---
import { HeadingDropdownMenu } from "../../tiptap-ui/heading-dropdown-menu";
// import { ImageUploadButton } from "../../tiptap-ui/image-upload-button";
import { BlockquoteButton } from "../../tiptap-ui/blockquote-button";
import { CodeBlockButton } from "../../tiptap-ui/code-block-button";
import {
  ColorHighlightPopover,
  ColorHighlightPopoverButton,
  ColorHighlightPopoverContent,
} from "../../tiptap-ui/color-highlight-popover";
import {
  LinkButton,
  LinkContent,
  LinkPopover,
} from "../../tiptap-ui/link-popover";
import { ListDropdownMenu } from "../../tiptap-ui/list-dropdown-menu";
import { MarkButton } from "../../tiptap-ui/mark-button";
import { TextAlignButton } from "../../tiptap-ui/text-align-button";
import { UndoRedoButton } from "../../tiptap-ui/undo-redo-button";

// --- Icons ---
import { ArrowLeftIcon } from "../../tiptap-icons/arrow-left-icon";
import { HighlighterIcon } from "../../tiptap-icons/highlighter-icon";
import { LinkIcon } from "../../tiptap-icons/link-icon";

// --- Hooks ---
import { useIsMobile } from "../../../hooks/use-mobile";

// --- Styles ---
import { AppProvider } from "../../../contexts/app-context";
import "./simple-editor.scss";

import { Spinner } from "@borderless/ui/components/spinner";
import { AiProvider } from "../../../contexts/ai-context";
import { useCollab } from "../../../contexts/collab-context";
import { generateRandomColor } from "../../../contexts/user-context";
import { cn } from "../../../lib/tiptap-utils";
import { EditorProvider, useEditorContext } from "../../EditorProvider";
import { ImageUploadButton } from "../../tiptap-ui/image-upload-button";
import { EditorProps } from "../../types";

const MainToolbarContent = ({
  onHighlighterClick,
  onLinkClick,
  isMobile,
  allowImageUpload = false,
}: {
  onHighlighterClick: () => void;
  onLinkClick: () => void;
  isMobile: boolean;
  allowImageUpload?: boolean;
}) => {
  return (
    <>
      <Spacer />
      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} />
        <ListDropdownMenu types={["bulletList", "orderedList", "taskList"]} />
        <BlockquoteButton />
        <CodeBlockButton />
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        <MarkButton type="underline" />
        {!isMobile ? (
          <ColorHighlightPopover />
        ) : (
          <ColorHighlightPopoverButton onClick={onHighlighterClick} />
        )}
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup>
      <ToolbarSeparator />
      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>
      {allowImageUpload && (
        <>
          <ToolbarSeparator />
          <ToolbarGroup>
            <ImageUploadButton />
          </ToolbarGroup>
        </>
      )}
      <Spacer />
    </>
  );
};

const MobileToolbarContent = ({
  type,
  onBack,
}: {
  type: "highlighter" | "link";
  onBack: () => void;
}) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === "highlighter" ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === "highlighter" ? (
      <ColorHighlightPopoverContent />
    ) : (
      <LinkContent />
    )}
  </>
);

export const SimpleEditorProvider: React.FC<
  Pick<
    EditorProps,
    "onUpdate" | "placeholder" | "user" | "connectToRemoteSync" | "contentId"
  > & {
    children: React.ReactNode;
  }
> = ({ children, placeholder = "Start writing...", ...props }) => {
  return (
    <AppProvider>
      <AiProvider>
        <EditorProvider
          {...props}
          placeholder={placeholder}
          user={{ ...props.user, color: generateRandomColor() }}
          editorProps={{
            attributes: {
              autocomplete: "off",
              autocorrect: "off",
              autocapitalize: "off",
              "aria-label": "Main content area, start typing to enter text.",
              class: "simple-editor",
            },
          }}
        >
          {children}
        </EditorProvider>
      </AiProvider>
    </AppProvider>
  );
};

export function SimpleEditorContent({
  className,
  loadingSkeleton,
}: {
  className?: string;
  loadingSkeleton?: React.ReactNode;
}) {
  const { editor } = useEditorContext();
  const { isSyncedLocal, error } = useCollab();

  // TODO: think more about isSynced === false state. Currently we are allowing it to fail silenty.
  // This is better for faster initial load, and sync is handled by the provider and Yjs.
  // It's fine if we eventually expect the connection to the collab server to be synced. It's an issue when the user never re-connects to the server from this device.

  if (!editor || !isSyncedLocal)
    return loadingSkeleton ?? <Spinner className="mx-auto my-8" />;

  if (error) {
    return (
      <div className="text-destructive p-8">
        <p>Error connecting to the editor: {error.message}</p>
      </div>
    );
  }

  return (
    <EditorContent
      editor={editor}
      role="presentation"
      className={cn("simple-editor-content [&_p]:text-xs", className)}
    />
  );
}

export function Toolbar({
  className,
  style,
  allowImageUpload = false,
}: {
  className?: string;
  style?: React.CSSProperties;
  allowImageUpload?: boolean;
}) {
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = React.useState<
    "main" | "highlighter" | "link"
  >("main");
  const toolbarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isMobile && mobileView !== "main") {
      setMobileView("main");
    }
  }, [isMobile, mobileView]);

  if (isMobile) return null;

  return (
    <div className={cn("z-10", className)} style={style}>
      <TiptapToolbar ref={toolbarRef}>
        <MainToolbarContent
          onHighlighterClick={() => setMobileView("highlighter")}
          onLinkClick={() => setMobileView("link")}
          isMobile={isMobile}
          allowImageUpload={allowImageUpload}
        />
      </TiptapToolbar>
    </div>
  );
}
